<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'product',
    path: '/products/:id',
    component: 'Product',
    params: [
      {
        name: 'id',
        prop: 'oid',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Product',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_parentTo = computed(() => ({
    name: 'app.products',
  }));

  const $data = computed(() => ({
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_parentTo: b_2248226175642056_parentTo.value,
  }));

  const $$load_item = () => {
    return $fn.value.fetch('read_product', {
      asResource: true,
      parameters: { id: props.oid },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_parentTo', nodeId: '2248226175642056' },
    ],
    loaders: { item: $$load_item },
    ux: {},
    dataRefs: {
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          item.value.name || item.value.number,
          $fn.value.gettext('Product'),
        ],
        icon: $config.value.product.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_kyir3e1n4_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_kyir3e1n4 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_kyir3e1n4_getActionArgs,
    });
  const inst_evh_7315092382398562_close_6s2v6ww8h =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_kyir3e1n4,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_6s2v6ww8h],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_224f7bspw =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_uzsgh6s5b =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_224f7bspw,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_uzsgh6s5b],
    event: 'reload',
  });
  const act_6466705138206980_reloadComponentData_ykwuvkmu9 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop_7zfsc8i0u =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_ykwuvkmu9,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6466705138206980_clickPreventStop_7zfsc8i0u],
      event: 'click.prevent.stop',
    });
  const act_8320016629450276_closeComponent_pmvhbubh4 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8320016629450276_success_x7fdm5wiq =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_closeComponent_pmvhbubh4,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_8320016629450276_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8320016629450276_success_x7fdm5wiq],
    event: 'success',
  });
  const act_6705964765214521_reloadComponentData_zqe94o531 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6705964765214521_success_me04wv0ao =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_reloadComponentData_zqe94o531,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6705964765214521_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6705964765214521_success_me04wv0ao],
    event: 'success',
  });
  const act_5477420070495986_reloadComponentData_eefghsshn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5477420070495986_reload_xdvfpzi4p =
    new actionTypes.EventHandlerImpl({
      action: act_5477420070495986_reloadComponentData_eefghsshn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5477420070495986_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5477420070495986_reload_xdvfpzi4p],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_6s2v6ww8h,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_uzsgh6s5b,
    inst_evh_7315092382398562_reload,
    inst_evh_6466705138206980_clickPreventStop_7zfsc8i0u,
    reload: inst_evh_6466705138206980_clickPreventStop_7zfsc8i0u,
    inst_evh_6466705138206980_clickPreventStop,
    inst_evh_8320016629450276_success_x7fdm5wiq,
    inst_evh_8320016629450276_success,
    inst_evh_6705964765214521_success_me04wv0ao,
    inst_evh_6705964765214521_success,
    inst_evh_5477420070495986_reload_xdvfpzi4p,
    inst_evh_5477420070495986_reload,
  };

  const evh_7315092382398562_close_6s2v6ww8h = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_6s2v6ww8h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_uzsgh6s5b = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_uzsgh6s5b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop_7zfsc8i0u = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop_7zfsc8i0u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success_x7fdm5wiq = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success_x7fdm5wiq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_success_me04wv0ao = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_success_me04wv0ao.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_success = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5477420070495986_reload_xdvfpzi4p = async (event, scope) => {
    return await actions.inst_evh_5477420070495986_reload_xdvfpzi4p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5477420070495986_reload = async (event, scope) => {
    return await actions.inst_evh_5477420070495986_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Product"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <MaterialTabs
      :title="item.snapshot('name')"
      :subtitle="item.snapshot('number')"
      :parentText="gettext('Products')"
      :parentTo="b_2248226175642056_parentTo"
      class="h-100"
      v-if="loaded"
    >
      <template #actions>
        <ItpButton
          variant="light"
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_6466705138206980_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <BtnDeleteResource
          :resource="item"
          :dialogHeader="pgettext('Modal|Header', 'Delete product')"
          @success="evh_8320016629450276_success($event, {})"
        >
        </BtnDeleteResource>
        <BtnSyncResource
          :resource="item"
          size="sm"
          @success="evh_6705964765214521_success($event, {})"
        >
        </BtnSyncResource>
      </template>
      <BTab :title="gettext('General')" class="container-lg float-start">
        <ItpRow>
          <ItpCol :w="6">
            <UcProductGeneral
              :product="item"
              :reload="lastLoaded"
              class="pb-5"
              @reload="evh_5477420070495986_reload($event, {})"
            >
            </UcProductGeneral>
          </ItpCol>
          <ItpCol :w="6">
            <UcCommentsList :parent="item" :reload="lastLoaded" class="pb-5">
            </UcCommentsList>
            <UcAttachmentsList :parent="item" :reload="lastLoaded" class="pb-5">
            </UcAttachmentsList>
          </ItpCol>
        </ItpRow>
      </BTab>
      <BTab :title="gettext('Prices')" class="container-fluid h-100">
        <UcProductPrices :product="item" :reload="lastLoaded">
        </UcProductPrices>
      </BTab>
    </MaterialTabs>
  </ItpPage>
</template>
